import { require as loadFile } from '@tiger/libs';
import lscache from 'lscache';
import API from 'utils/API';
import http, { HttpError } from 'utils/http';
import { language } from 'utils/i18n';

export enum CaptchaType {
    'GEETEST' = 'geetest', // 极验 文档 https://docs.geetest.com/gt4/apirefer/api/web
    // recaptcha 文档 https://cloud.google.com/recaptcha-enterprise/docs/quickstarts
    'RECAPTCHA_SCORE' = 'recaptcha-score', // google recaptcha score 用户无感知
    'RECAPTCHA_CHECKBOX' = 'recaptcha-checkbox' // google recaptcha checkbox
}

/**
 * 请求captcha的类型
 */
export enum CaptchaRequestType {
    GEETEST4 = 'geetest4', // 极验4
    RECAPTCHA = 'recaptcha'
}

export enum CaptchaResponseType {
    GEETEST4 = 'geetest4', // 极验4
    GEETEST = 'geetest', // 由于recaptcha score 模式没通过的话，接口会返回下一步使用极验geetest为了兼容老版本这里后端没改为geetest4
    RECAPTCHA = 'recaptcha', // google recaptcha
    RECAPTCHA_CHECKBOX = 'recaptcha_checkbox' // google 九宫格
}

export type SiteKeys = Array<{ site_key: string; type: 'score' | 'checkbox' }>;

export interface CaptchaConfig {
    type: CaptchaResponseType;
    // recaptcha配置
    site_keys?: SiteKeys;
    // 极验4.0 配置
    geetest4_sdk?: {
        captcha_id: string;
        risk_type: string;
    };
}

// 极验验证后返回的数据
export interface GeeTest4Response {
    captcha_id: string;
    lot_number: string;
    pass_token: string;
    gen_time: string;
    captcha_output: string;
}

const langMapToRecaptcha = {
    en_US: 'en',
    zh_CN: 'zh-CN',
    zh_TW: 'zh-TW'
};

export interface GeeCaptchaObj {
    appendTo(container: string | HTMLElement): void;
    getValidate(): GeeTest4Response;
    reset(): void;
    showCaptcha(): void;
    onReady(callback: () => void): GeeCaptchaObj;
    onNextReady(callback: () => void): GeeCaptchaObj;
    onSuccess(callback: () => void): GeeCaptchaObj;
    onFail(callback: () => void): GeeCaptchaObj;
    onError(callback: (error: Error) => void): GeeCaptchaObj;
    onClose(callback: () => void): GeeCaptchaObj;
    destroy(): void;
}

interface RecaptchaRenderConfig {
    sitekey: string;
    action?: string;
    theme?: 'dark' | 'light';
    size?: string;
    tabindex?: number;
    callback?(token: string): void;
    'expired-callback'?(error): void;
    'error-callback'?(error): void;
}

declare global {
    const grecaptcha: {
        enterprise: {
            ready(callback: () => void);
            reset(optWidgetId?: number): void;
            execute(sitekey?: string, config?: { action: string }): Promise<string>;
            getResponse(optWidgetId: string): string;
            render(container: string | HTMLElement, config: RecaptchaRenderConfig): number;
        };
    };
}

export enum CaptchaErrorCode {
    SITE_KEY_MISSING = 1001, // siteKey 不存在
    SCRIPT_LOAD_FAILED = 1002, // 脚本加载失败
    EXECUTE_FAILED = 1003, // execute 执行失败
    RENDER_FAILED = 1004, // render 渲染失败
    SCRIPT_LOAD_TIMEOUT = 1005 // 脚本加载超时
}

const RECAPTCHA_FAILURE_COUNT_KEY = 'RECAPTCHA_FAILURE_COUNT';
const RECAPTCHA_FAILURE_THRESHOLD = 2; // 失败次数阈值
const RECAPTCHA_FAILURE_EXPIRE_MINUTES = 5; // 5分钟有效期

// 记录 Recaptcha 失败
export function recordRecaptchaFailure() {
    const count = lscache.get(RECAPTCHA_FAILURE_COUNT_KEY) || 0;

    // 设置缓存，5分钟过期
    lscache.set(RECAPTCHA_FAILURE_COUNT_KEY, count + 1, RECAPTCHA_FAILURE_EXPIRE_MINUTES);
}

// 清除 Recaptcha 失败记录
export function clearRecaptchaFailureRecord() {
    lscache.remove(RECAPTCHA_FAILURE_COUNT_KEY);
}

// 检查 Recaptcha 是否可用
export function isRecaptchaAvailable(): boolean {
    const count = lscache.get(RECAPTCHA_FAILURE_COUNT_KEY) || 0;

    return count < RECAPTCHA_FAILURE_THRESHOLD;
}

function createCaptchaError(code: CaptchaErrorCode, message?: string) {
    const defaultMessage = __('验证码初始化失败，请稍后刷新页面重试!');

    const error = new Error(`${message || defaultMessage}(${code})`) as HttpError;

    error.error_code = code;

    return error;
}

const SCRIPT_LOAD_TIMEOUT = 30_000;

// recaptch score 验证 并获取内部验证token
export function recaptchaByScore(
    siteKeys: SiteKeys,
    action: string
): Promise<{ nextStep?: string; captchaToken?: string }> {
    const lang = langMapToRecaptcha[language] || langMapToRecaptcha.en_US;
    const scoreSiteKey = siteKeys.find(item => item.type === 'score')?.site_key;

    return new Promise((resolve, reject) => {
        if (!scoreSiteKey) {
            reject(createCaptchaError(CaptchaErrorCode.SITE_KEY_MISSING));
        } else {
            loadFile(`https://www.google.com/recaptcha/enterprise.js?render=${scoreSiteKey}&hl=${lang}`)
                .then(() => {
                    const timer = setTimeout(() => {
                        reject(createCaptchaError(CaptchaErrorCode.SCRIPT_LOAD_TIMEOUT));
                    }, SCRIPT_LOAD_TIMEOUT);

                    grecaptcha.enterprise.ready(async function() {
                        clearTimeout(timer);

                        try {
                            const token = await grecaptcha.enterprise.execute(scoreSiteKey, { action });

                            const params = {
                                recaptcha_site_key: scoreSiteKey,
                                recaptcha_token: token,
                                recaptcha_action: action
                            };

                            try {
                                // 获取到 recaptcha score token 去验证token 是否有效
                                const { data } = await http.post(API.OAUTH.verifyCaptcha(), params);

                                if (data.captcha_token) {
                                    resolve({ captchaToken: data.captcha_token });
                                } else if (data.next_step) {
                                    resolve({ nextStep: data.next_step[0] });
                                }
                            } catch (error) {
                                reject(error);
                            }
                        } catch {
                            reject(createCaptchaError(CaptchaErrorCode.EXECUTE_FAILED));
                        }
                    });
                })
                .catch(() => {
                    reject(createCaptchaError(CaptchaErrorCode.SCRIPT_LOAD_FAILED));
                });
        }
    });
}

export function recaptchaByCheckbox(container: string | HTMLElement, config: RecaptchaRenderConfig): Promise<number> {
    const lang = langMapToRecaptcha[language] || langMapToRecaptcha.en_US;

    return new Promise<number>((resolve, reject) => {
        loadFile(`https://www.google.com/recaptcha/enterprise.js?render=explicit&hl=${lang}`)
            .then(() => {
                const timer = setTimeout(() => {
                    reject(createCaptchaError(CaptchaErrorCode.SCRIPT_LOAD_TIMEOUT));
                }, SCRIPT_LOAD_TIMEOUT);

                grecaptcha.enterprise.ready(function() {
                    clearTimeout(timer);

                    try {
                        const recaptchaId = grecaptcha.enterprise.render(container, config);

                        resolve(recaptchaId);
                    } catch {
                        reject(createCaptchaError(CaptchaErrorCode.RENDER_FAILED));
                    }
                });
            })
            .catch(() => {
                reject(createCaptchaError(CaptchaErrorCode.SCRIPT_LOAD_FAILED));
            });
    });
}
